import { default as React } from 'react';
import { useMemo, useState, useEffect } from 'react';
import { useEntity, catalogApiRef } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { ComponentAboutCard } from '../ComponentAboutCard';
import {
  useApi,
  configApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { Button, Box, Grid, CircularProgress } from '@material-ui/core';
import LaunchIcon from '@mui/icons-material/Launch';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import { ManageOwnersButton } from './ManageOwnersButton';
import { makeStyles } from '@mui/styles';
import {
  useCurrentUserEmail,
  isUserAuthorizedForEntity,
  useSkyStageAdminEmails,
  useMicroappEnvironmentStatus,
} from './hooks';
import {
  buildMicroappRedeployFormData,
  GCP_PROJECT_ID_RE,
} from './microappRedeployForm';

const MICROAPP_ANNOTATION_KEY = 'skystage.wellsky.com/type';
const MICROAPP_ANNOTATION_VALUE = 'microapp';

const useStyles = makeStyles(theme => ({
  disableEditButton: {
    '& [title="Edit Metadata"]': {
      pointerEvents: 'none',
      opacity: 0.38,
      cursor: 'not-allowed',
    },
  },
  launchButton: {
    marginTop: theme.spacing(2),
    width: '100%',
  },
  blueButton: {
    textTransform: 'none',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  redeployButton: {
    textTransform: 'none',
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.secondary.dark,
    },
  },
}));

export const MicroappAboutCard = ({ variant }: { variant?: 'gridItem' }) => {
  const { entity } = useEntity();
  const classes = useStyles();
  const navigate = useNavigate();
  const currentUserEmail = useCurrentUserEmail();
  const adminEmails = useSkyStageAdminEmails();
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [isRedeploying, setIsRedeploying] = useState(false);

  // After a successful redeploy the entity page may show stale data because
  // catalogApi.refreshEntity() only updates the backend — EntityProvider does
  // not re-fetch the entity in the current React session.
  // Force EntityProvider to remount (and call getEntityByRef() fresh) by
  // navigating to the catalog root and immediately back to this entity page.
  useEffect(() => {
    const key = `redeployed-${entity.metadata.name}`;
    if (!sessionStorage.getItem(key)) return;
    sessionStorage.removeItem(key);

    catalogApi.refreshEntity(stringifyEntityRef(entity)).catch(() => {});

    const { namespace = 'default', name } = entity.metadata;
    const entityPath = `/catalog/${namespace}/${entity.kind.toLowerCase()}/${name}`;
    navigate('/catalog', { replace: true });
    setTimeout(() => navigate(entityPath), 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check if this is a microapp
  const isMicroapp =
    entity.metadata.annotations?.[MICROAPP_ANNOTATION_KEY] ===
    MICROAPP_ANNOTATION_VALUE;

  const isHostedApp =
    entity.metadata.annotations?.['microapp/appType'] === 'Hosted';

  // User may redeploy if they own the app, created it, or are a SkyStage admin.
  // isUserAuthorizedForEntity centralizes the owner/creator/admin logic so the
  // Redeploy button stays consistent with every other owner-only action.
  const isAuthorized = isUserAuthorizedForEntity(
    entity,
    currentUserEmail,
    adminEmails,
  );

  // Rename for clarity (backward compatibility)
  const isCreator = isAuthorized;

  // Get the app URL for microapps
  let appUrl = '';
  let linkText = 'Launch App';

  if (isMicroapp) {
    try {
      const columnsAnnotation =
        entity.metadata.annotations?.['backstage.io/columns'];
      if (columnsAnnotation) {
        const cleanedAnnotation = columnsAnnotation
          .replace(/\n\s*/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();

        const cols = JSON.parse(cleanedAnnotation);
        const accessAppCol = cols.find(
          (c: any) =>
            c.title && c.title.replace(/\s+/g, ' ').trim() === 'Access App',
        );

        if (accessAppCol && accessAppCol.value) {
          appUrl = accessAppCol.value;
          linkText = accessAppCol.linkText || 'Launch App';
        }
      }
    } catch (parseError) {
      // Continue to fallback
    }

    // Fallback: use view-url annotation
    if (!appUrl) {
      appUrl = entity.metadata.annotations?.['backstage.io/view-url'] || '';
    }
  }

  // IDP-983: live per-environment status (from ArgoCD) drives the launch
  // buttons for hosted apps.
  const { status: envStatus } = useMicroappEnvironmentStatus(
    entity.metadata.name,
  );
  const prodDeployed = envStatus?.production?.deployed ?? false;
  const prodUrl = envStatus?.production?.url ?? null;
  const stagingDeployed = envStatus?.staging?.deployed ?? false;
  const stagingUrl = envStatus?.staging?.url ?? null;
  // Launch · Production is public; Launch · Staging is admin-only WHEN production
  // also exists, but is the primary (public) launch for a staging-first app.
  const showProdLaunch = isHostedApp && prodDeployed && !!prodUrl;
  const showStagingLaunch =
    isHostedApp &&
    stagingDeployed &&
    !!stagingUrl &&
    (isAuthorized || !prodDeployed);

  // Extract all configuration values from entity annotations (memoized to avoid
  // re-parsing on every render)
  // IDP-983: annotation → template formData mapping is shared with the admin
  // page Environments widget (see microappRedeployForm.ts).
  const microappConfig = useMemo(
    () => buildMicroappRedeployFormData(entity),
    [entity],
  );

  const handleRedeploy = async () => {
    if (!microappConfig) return;

    setIsRedeploying(true);
    try {
      let prefillConfig = { ...microappConfig };

      // Always check the live GCP SM API for managed secrets — even when the
      // annotation says enableSecrets=false or the annotation is absent.
      // This covers two important cases:
      //   1. Legacy apps deployed before the microapp/hasGsmSecrets annotation
      //      was introduced — they have secrets in GCP SM but no annotation,
      //      so annotation-based pre-fill leaves the checkbox unchecked.
      //   2. Catalog staleness — the entity may not yet reflect a mode switch.
      // The live API is always the source of truth for managed secrets.
      //
      // IDP-983: Use dual-env endpoint which returns LOGICAL keys (API_KEY)
      // not raw GCP keys (STAGING_API_KEY). The old endpoint returned raw keys
      // which caused double STAGING_ prefix bugs in the SecretProviderClass.
      try {
        const { token } = await identityApi.getCredentials();
        const backendUrl = config.getString('backend.baseUrl');
        const appName = entity.metadata.name;
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        };
        // First try dual-env endpoint (returns logical keys)
        let liveKeys: string[] = [];
        const dualEnvResponse = await fetch(
          `${backendUrl}/api/microapps-api/secrets/${appName}/dual-env`,
          { headers },
        );
        if (dualEnvResponse.ok) {
          const dualEnvData = await dualEnvResponse.json();
          // Dual-env returns { secrets: [{ key, stagingExists, productionExists, ... }] }
          liveKeys = (dualEnvData.secrets ?? []).map(
            (s: { key: string }) => s.key,
          );
        } else if (dualEnvResponse.status === 400) {
          // Dual-env not supported (MCP server or BYOG mode) — fall back to legacy endpoint
          const legacyResponse = await fetch(
            `${backendUrl}/api/microapps-api/secrets/${appName}`,
            { headers },
          );
          if (legacyResponse.ok) {
            const data = await legacyResponse.json();
            liveKeys = (data.secrets ?? []).map((s: { key: string }) => s.key);
          }
        }
        // Fetch BYOG secrets from the legacy endpoint (dual-env doesn't support BYOG)
        const byogResponse = await fetch(
          `${backendUrl}/api/microapps-api/secrets/${appName}`,
          { headers },
        );
        let liveByogKeys: string[] = [];
        let liveByogProjectId = '';
        if (byogResponse.ok) {
          const byogData = await byogResponse.json();
          liveByogKeys = (byogData.byogSecrets ?? []).map(
            (s: { key: string }) => s.key,
          );
          liveByogProjectId = byogData.byogGcpProjectId ?? '';
        }
        if (liveKeys.length > 0) {
          // Live managed secrets exist — force SkyStage Managed mode and
          // check the secrets checkbox regardless of annotation state.
          // This is the correct state for legacy apps and stale catalogs.
          // IDP-983: Using dual-env endpoint ensures keys are LOGICAL (API_KEY)
          // not raw GCP keys (STAGING_API_KEY).
          prefillConfig = {
            ...prefillConfig,
            enableSecrets: true,
            secretsMode: 'SkyStage Managed',
            // Dual-env format for secrets: key + empty staging/production values
            secrets: liveKeys.map(key => ({
              key,
              stagingValue: '',
              productionValue: '',
            })),
            byogGcpProjectId: undefined,
            byogSecretNames: undefined,
          };
        } else if (liveByogKeys.length > 0) {
          // API returned BYOG secrets — app is in BYOG mode. Use live data
          // even if the catalog annotation is stale (e.g. after a mode switch
          // that hasn't propagated to the catalog yet).
          prefillConfig = {
            ...prefillConfig,
            enableSecrets: true,
            secretsMode: 'BYOG (My Own GCP Project)',
            byogGcpProjectId: liveByogProjectId,
            byogSecretNames: liveByogKeys.join(','),
            secrets: undefined,
          };
        } else {
          // API returned no live secrets (managed or BYOG). Check for the
          // legacy BYOG case where the user's GCP project ID was stored in
          // the now-deprecated microapp/userGcpProjectId annotation.
          const legacyByogGcpProjectId =
            entity.metadata.annotations?.['microapp/userGcpProjectId'] || '';
          if (
            legacyByogGcpProjectId &&
            GCP_PROJECT_ID_RE.test(legacyByogGcpProjectId) &&
            prefillConfig.enableSecrets &&
            prefillConfig.secretsMode === 'SkyStage Managed' &&
            Array.isArray(prefillConfig.secrets) &&
            (prefillConfig.secrets as Array<{ key: string }>).length > 0
          ) {
            // Legacy BYOG app: migrate annotation-based key list to BYOG form fields.
            const legacySecretNames = (
              prefillConfig.secrets as Array<{ key: string }>
            )
              .map(s => s.key)
              .join(',');
            prefillConfig = {
              ...prefillConfig,
              enableSecrets: true,
              secretsMode: 'BYOG (My Own GCP Project)',
              byogGcpProjectId: legacyByogGcpProjectId,
              byogSecretNames: legacySecretNames,
              secrets: undefined,
            };
          } else if (
            prefillConfig.secretsMode === 'SkyStage Managed' &&
            Array.isArray(prefillConfig.secrets) &&
            (prefillConfig.secrets as Array<{ key: string }>).length > 0
          ) {
            // No live managed secrets in GCP SM, but stale catalog annotation
            // says Managed mode with keys. The app has switched away from
            // Managed mode (e.g., switched to BYOG) but the catalog entity
            // hasn't refreshed yet. Clear the stale managed config so the form
            // doesn't wrongly pre-select Managed with ghost keys.
            prefillConfig = {
              ...prefillConfig,
              enableSecrets: false,
              secrets: undefined,
              secretsMode: undefined,
            };
          }
          // Otherwise (including legacy GSM apps whose secrets predate the
          // qp-{appShortId}-KEY naming convention): trust the annotation-based
          // pre-fill — it is the best available source of truth when the live
          // API returns nothing. Do NOT clear the form.
        }
      } catch {
        // Fall back to annotation-based pre-fill on any fetch error
      }

      const formDataWithAction = {
        ...prefillConfig,
        actionType: 'redeploy',
      };
      const templateUrl = `/create/templates/default/microapp-hosting?formData=${encodeURIComponent(
        JSON.stringify(formDataWithAction),
      )}`;
      // Mark this app as pending a catalog refresh so that when the user
      // returns to the entity page after the template completes, a reload
      // is triggered to show the latest entity data.
      sessionStorage.setItem(`redeployed-${entity.metadata.name}`, '1');
      navigate(templateUrl);
    } finally {
      setIsRedeploying(false);
    }
  };

  return (
    <Box>
      {/* Keep existing About Card - Edit Metadata button disabled for microapps */}
      <Box className={isMicroapp ? classes.disableEditButton : undefined}>
        <ComponentAboutCard variant={variant} />
      </Box>

      {/* Launch and Redeploy Buttons */}
      {isMicroapp && (
        <Box className={classes.launchButton}>
          <Grid container spacing={1}>
            {/* Non-hosted apps: single launch from the view-url. */}
            {!isHostedApp && appUrl && (
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  startIcon={<LaunchIcon />}
                  href={appUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  fullWidth
                  className={classes.blueButton}
                >
                  {linkText}
                </Button>
              </Grid>
            )}
            {/* Hosted apps: Launch · Production (public). */}
            {showProdLaunch && (
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  startIcon={<LaunchIcon />}
                  href={prodUrl as string}
                  target="_blank"
                  rel="noopener noreferrer"
                  fullWidth
                  className={classes.blueButton}
                >
                  Launch
                </Button>
              </Grid>
            )}
            {/* Hosted apps: Launch · Staging. Admin-only when production also
                exists; the primary (public) launch for a staging-first app. */}
            {showStagingLaunch && (
              <Grid item xs={12} sm={6}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="medium"
                  startIcon={<LaunchIcon />}
                  href={stagingUrl as string}
                  target="_blank"
                  rel="noopener noreferrer"
                  fullWidth
                >
                  Launch · Staging
                </Button>
              </Grid>
            )}
            {isHostedApp && isCreator && microappConfig && (
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={
                    isRedeploying ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <RefreshIcon />
                    )
                  }
                  onClick={handleRedeploy}
                  disabled={isRedeploying}
                  fullWidth
                  className={classes.redeployButton}
                >
                  {isRedeploying ? 'Loading...' : 'Redeploy'}
                </Button>
              </Grid>
            )}
            {isMicroapp && isAuthorized && (
              <Grid item xs={12}>
                <ManageOwnersButton
                  entity={entity}
                  currentUserEmail={currentUserEmail}
                  isAuthorized={isAuthorized}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </Box>
  );
};
